// src/api/sso.ts
//
// Browser-side OIDC Authorization Code + PKCE flow for SSO (Entra ID / Okta /
// any OIDC IdP). The IdP enforces MFA; on return we hand the code to the
// backend (/auth/sso/callback), which mints our normal app access token.
//
// Provider-neutral: everything comes from the backend, so the same code works
// against Entra ID or Okta with no changes. When customers have their own IdP
// (a "connection"), the user's work email is looked up via /auth/sso/discover
// to pick which one to send them to.

import { authApi, type LoginResponse, type SsoPublicConfig } from "./auth";

const PKCE_VERIFIER_KEY = "sso_pkce_verifier";
const STATE_KEY = "sso_state";
const NONCE_KEY = "sso_nonce";
const CONNECTION_ID_KEY = "sso_connection_id";
const REDIRECT_URI_KEY = "sso_redirect_uri";
const TOKEN_ENDPOINT_KEY = "sso_token_endpoint";
const CLIENT_ID_KEY = "sso_client_id";
const SCOPES_KEY = "sso_scopes";

function base64UrlEncode(bytes: Uint8Array): string {
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteLen = 32): string {
  const arr = new Uint8Array(byteLen);
  crypto.getRandomValues(arr);
  return base64UrlEncode(arr);
}

async function sha256Challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

export interface SsoAvailability {
  /** Show the "Sign in with SSO" button. */
  enabled: boolean;
  /** Only customer connections back that button, so it needs a work email. */
  needsEmail: boolean;
}

/** Whether SSO is available on the backend (drives the login-page button). */
export async function getSsoAvailability(): Promise<SsoAvailability> {
  try {
    const cfg = await authApi.ssoConfig();
    const globalReady = !!cfg.enabled && !!cfg.authorization_endpoint && !!cfg.client_id;
    const discovery = !!cfg.discovery;
    return { enabled: globalReady || discovery, needsEmail: discovery && !globalReady };
  } catch {
    return { enabled: false, needsEmail: false };
  }
}

/**
 * Kick off the redirect to the IdP. With a work email, the backend picks the
 * IdP (the email's company connection, else the global one); without one, the
 * global SSO config is used. Generates PKCE + state + nonce, stashes them in
 * sessionStorage, and navigates the browser to the IdP's authorize endpoint.
 */
export async function startSsoLogin(email?: string): Promise<void> {
  const trimmed = (email || "").trim();
  let cfg: SsoPublicConfig;
  if (trimmed) {
    cfg = await authApi.ssoDiscover(trimmed);
  } else {
    cfg = await authApi.ssoConfig();
    if (!cfg.enabled && cfg.discovery) {
      throw new Error("Enter your work email above, then choose Sign in with SSO.");
    }
  }
  if (!cfg.enabled || !cfg.authorization_endpoint || !cfg.client_id) {
    throw new Error("SSO is not available right now.");
  }

  const verifier = randomString(32);
  const challenge = await sha256Challenge(verifier);
  const state = randomString(16);
  const nonce = randomString(16);
  const redirectUri = cfg.redirect_uri || `${window.location.origin}/`;

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(NONCE_KEY, nonce);
  if (cfg.connection_id) sessionStorage.setItem(CONNECTION_ID_KEY, cfg.connection_id);
  else sessionStorage.removeItem(CONNECTION_ID_KEY);
  sessionStorage.setItem(REDIRECT_URI_KEY, redirectUri);
  sessionStorage.setItem(CLIENT_ID_KEY, cfg.client_id);
  sessionStorage.setItem(SCOPES_KEY, cfg.scopes || "openid profile email");
  if (cfg.token_endpoint) sessionStorage.setItem(TOKEN_ENDPOINT_KEY, cfg.token_endpoint);

  const params = new URLSearchParams({
    client_id: cfg.client_id,
    response_type: "code",
    redirect_uri: redirectUri,
    scope: cfg.scopes || "openid profile email",
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: "S256",
    response_mode: "query",
  });

  window.location.assign(`${cfg.authorization_endpoint}?${params.toString()}`);
}

/** True if the current URL looks like an IdP redirect back to us. */
export function hasSsoCallbackParams(): boolean {
  const p = new URLSearchParams(window.location.search);
  return (p.has("code") || p.has("error")) && p.has("state");
}

function clearPkceState(): void {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(NONCE_KEY);
  sessionStorage.removeItem(CONNECTION_ID_KEY);
  sessionStorage.removeItem(REDIRECT_URI_KEY);
  sessionStorage.removeItem(TOKEN_ENDPOINT_KEY);
  sessionStorage.removeItem(CLIENT_ID_KEY);
  sessionStorage.removeItem(SCOPES_KEY);
}

/**
 * Exchange the auth code for tokens IN THE BROWSER. SPA (public) clients must
 * redeem at the IdP token endpoint via a cross-origin request — the backend is
 * not allowed to (Entra AADSTS9002327). No client secret is used (PKCE only).
 */
async function exchangeCodeForIdToken(code: string): Promise<string> {
  const tokenEndpoint = sessionStorage.getItem(TOKEN_ENDPOINT_KEY);
  const clientId = sessionStorage.getItem(CLIENT_ID_KEY);
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const redirectUri = sessionStorage.getItem(REDIRECT_URI_KEY) || `${window.location.origin}/`;
  const scopes = sessionStorage.getItem(SCOPES_KEY) || "openid profile email";

  if (!tokenEndpoint || !clientId || !verifier) {
    throw new Error("SSO session expired — please try signing in again.");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: verifier,
    scope: scopes,
  });

  const res = await fetch(tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.id_token) {
    const desc = data.error_description || data.error || `HTTP ${res.status}`;
    throw new Error(`SSO token exchange failed: ${desc}`);
  }
  return data.id_token as string;
}

/** Strip the OAuth params from the address bar without reloading. */
function cleanUrl(): void {
  window.history.replaceState({}, document.title, window.location.pathname);
}

/**
 * Complete the SSO callback: validate state, exchange the code via the backend,
 * and return the same LoginResponse shape as a normal login. Always clears the
 * PKCE state and the URL params, success or failure.
 */
export async function completeSsoCallback(): Promise<LoginResponse> {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  const code = params.get("code");
  const returnedState = params.get("state");

  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const nonce = sessionStorage.getItem(NONCE_KEY);
  const connectionId = sessionStorage.getItem(CONNECTION_ID_KEY);

  try {
    if (error) {
      throw new Error(params.get("error_description") || `SSO error: ${error}`);
    }
    if (!code || !returnedState || !expectedState || returnedState !== expectedState) {
      throw new Error("SSO state mismatch — please try signing in again.");
    }
    if (!verifier) {
      throw new Error("SSO session expired — please try signing in again.");
    }
    if (connectionId) {
      // Customer connection (a confidential Web Application): the backend
      // redeems the code with the connection's client secret, so the browser
      // hands over the code and its PKCE verifier instead.
      if (!nonce) throw new Error("SSO session expired — please try signing in again.");
      return await authApi.ssoCallback({
        connection_id: connectionId,
        code,
        code_verifier: verifier,
        redirect_uri: sessionStorage.getItem(REDIRECT_URI_KEY) || `${window.location.origin}/`,
        nonce,
      });
    }
    // Global SSO (SPA public client): redeem the code at the IdP from the
    // browser, then hand the signed id_token to our backend to validate +
    // provision + mint a token.
    const idToken = await exchangeCodeForIdToken(code);
    return await authApi.ssoCallback({ id_token: idToken, nonce });
  } finally {
    clearPkceState();
    cleanUrl();
  }
}
